import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import { Dispatch, ReactElement, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import ConfirmModal from '~/ui/components/common/ConfirmModal';
import DatePicker from '~/ui/components/inputs/DatePicker';
import Select from '~/ui/components/inputs/SelectWithoutAnimation/SelectWithoutAnimation';

import { IRequestParams } from '~/services/api/clientDetails/types';
import { IDictionaryTypes } from '~/services/api/dictionaries/types';
import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';
import formatDictionaryOptions from '~/utils/formatDictionaryOptions';
import { teamDischangeValidationSchema } from '../../HealthDetails/Forms/validates';
import { IDetailsInfo } from '../types';
import { normalizeDateStringWithCurrentUtcTime } from '~/utils/date/date';

import styles from '../TeamsDetails.module.scss';

interface ITeamConfirmationModalProps {
  detailsInfo: IDetailsInfo | null;
  clinicId: string;
  teamId: string;
  clientId: string;
  onClose: () => void;
  setDetailsInfo: Dispatch<SetStateAction<IDetailsInfo>>;
}

const TeamConfirmationModal = ({
  detailsInfo,
  clinicId,
  clientId,
  teamId,
  onClose,
  setDetailsInfo,
}: ITeamConfirmationModalProps): ReactElement => {
  const [loading, setLoading] = useState(false);

  const isArchive = detailsInfo?.method === 'archive';
  const confirmText = isArchive ? 'Archive' : 'Restore';

  const { onArchiveTeam, onRestoreTeam } = useStoreActions(actions => actions.clientDetails);

  const { onGetClient } = useStoreActions(actions => actions.client);

  const description = `Are you sure you want to ${confirmText.toLowerCase()} “${
    detailsInfo?.name
  }” ${detailsInfo?.title || detailsInfo?.typeDetails}?`;

  const methods = {
    team: {
      restore: onRestoreTeam,
      archive: onArchiveTeam,
    },
  };

  const {
    control,
    formState: { errors },
    watch,
  } = useForm({
    resolver: teamDischangeValidationSchema,
  });

  const { dischargeReasonId, dischargeDate } = watch();

  const method = get(methods, [detailsInfo?.typeDetails, detailsInfo?.method], () => {});

  const { showNotify, showError } = useStoreActions(actions => actions.snackbar);
  const onGetDictionariesByType = useStoreActions(
    actions => actions.dictionaries.onGetDictionariesByType,
  );

  const referralSourceDictionary =
    useStoreState(
      state => state.dictionaries.availableDictionaries[IDictionaryTypes.DischargeReason]?.list,
    ) || [];

  const dischargeReasonOptions = formatDictionaryOptions(referralSourceDictionary);

  const onConfirmModal = async (values: Required<IRequestParams>) => {
    try {
      const type = isArchive ? 'archived' : 'restored';

      const payload = {
        ...values,
        ...(isArchive
          ? {
              dischargeReasonId,
              dischargeDate: normalizeDateStringWithCurrentUtcTime(dischargeDate),
            }
          : {}),
      };
      await method(payload);

      showNotify({
        message: `${capitalize(
          detailsInfo?.title || detailsInfo?.typeDetails,
        )} successfully ${type}`,
      });

      setDetailsInfo(null);

      await onGetClient({
        clientId,
        teamId,
        clinicId,
      });
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const onMount = useCallback(async () => {
    try {
      setLoading(true);
      await onGetDictionariesByType({ type: IDictionaryTypes.DischargeReason });
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [onGetDictionariesByType, showError]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  const DischargeDetails = () => (
    <>
      <div className={styles.input}>
        <Select
          isRelativeWindow
          maxMenuHeight={350}
          options={dischargeReasonOptions}
          name="dischargeReasonId"
          control={control}
          label="Reason for Discharge"
          errors={errors}
          hideSelectedOptions={false}
        />
      </div>
      <div className={styles.input}>
        <DatePicker
          name="dischargeDate"
          openTo="date"
          control={control}
          errors={errors}
          label="Date of Discharge"
          // maxDate={new Date().toISOString()}
        />
      </div>
    </>
  );

  return (
    <ConfirmModal
      onConfirm={() => {
        onConfirmModal({
          clientId,
          clinicId,
          teamId,
          id: String(detailsInfo?.id),
        });
      }}
      onClose={onClose}
      description={description}
      confirmText={confirmText}
      disabled={(loading || !dischargeReasonId || !dischargeDate) && isArchive}
      customContent={isArchive ? <DischargeDetails /> : null}
    />
  );
};

export default TeamConfirmationModal;
